import { FC, useState, useContext, useEffect, useMemo } from 'react'
import { Container } from 'react-bootstrap'
import styled from 'styled-components'
import moment from 'moment'
import DataTable, { IDataTableColumn, IDataTableStyles, IDataTableConditionalRowStyles } from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faSortDown } from '@fortawesome/free-solid-svg-icons'
import LoadingOverlay from 'react-loading-overlay'
import { PropagateLoader } from 'react-spinners'
import { useHistory } from 'react-router-dom'

import { simMod, moduleContent } from '../../Module.config'
import { ModuleContext } from '../../contexts/ModuleContext'
import * as LRS from '../../api/api'
import { FaultAttemptItem } from '../../api/api'

import NavigationBar from '../NavigationBar'
import { FaultState, FaultHistoryItem } from '../../Types'
import { ModuleClass } from '../../ModuleTypes'
import { PrimaryButton, ButtonGroup, SecondaryButton } from '../../styles/Buttons'
import { isDebug } from '../../App.config'
import { SimutechOrangeActive, SimutechOrange, SimutechBlue, SimutechBlueHighlight, SelectedRowStyle } from '../../styles/Theme'
import { useTroubleshootContext, TroubleshootActionType } from '../../contexts/TroubleshootContext'



const FaultHistory: FC = () => {
    const history = useHistory()
    const context = useContext(ModuleContext) // Needed to be able to modify fault ID directly in that context until other components are converted to React.FC with hooks.
    const { user } = context
    if (simMod.type === ModuleClass.LL) history.push('/')

    const { tsState, tsDispatch } = useTroubleshootContext()

    const [error, setError] = useState('')

    const [selectedFaultAttempt, setSelectedFaultAttempt] = useState({} as FaultHistoryItem)

    const goBack = () => history.push('/scoreboard')

    useEffect(() => {
        if (!user.userId || tsState.faultHistory) return
        isDebug && console.log('Fault History data load effect.')

        const fetchData = async () => {
            const faultHistorySummary: FaultAttemptItem[] = await LRS.getFaultAttemptListByUser(user.userId, user.companyId, simMod.solverModule)

            const rows: FaultHistoryItem[] = faultHistorySummary.map((faultAttempt: FaultAttemptItem): FaultHistoryItem => {
                const { _id, timestamp, faultId, faultState, skillRating } = faultAttempt
                const state = faultState === FaultState.NotRepaired ? 'Not Repaired' : faultState
                const rating = skillRating ? `${skillRating}%` : ''
                return {
                    id: _id,
                    date: moment(timestamp).format('YYYY-MM-DD'),
                    startTime: moment(timestamp).format('HH:mm:ss'),
                    faultId: faultId,
                    faultState: state,
                    skillRating: rating
                } as FaultHistoryItem
            })

            tsDispatch({ type: TroubleshootActionType.SAVE_FAULT_HISTORY, payload: rows })

        }
        fetchData()
            .catch(error => setError(error.toString()))

    }, [tsDispatch, tsState.faultHistory, user.companyId, user.userId])

    const onRowClick = (row: FaultHistoryItem, event: MouseEvent) => {
        event.preventDefault()
        setSelectedFaultAttempt(row)
    }

    const saveFaultId = (faultId: string) => {
        context.faultID = faultId //TODO: This is bad, but the faultID in ModuleContext is still needed in Simulation and UnityComponent until they are converted to React.FC with hooks.        
        tsDispatch({ type: TroubleshootActionType.SAVE_FAULT_ID, payload: faultId })
    }

    const handleRetry = (e: MouseEvent) => {
        e.preventDefault()
        saveFaultId(selectedFaultAttempt.faultId)
        history.push('/workorder')
    }

    const handleReview = (e: MouseEvent) => {
        e.preventDefault()
        history.push(`/faultattempt/${selectedFaultAttempt.id}`)
    }

    const columns: IDataTableColumn[] = useMemo(() => [
        {
            name: 'ID',
            omit: true,
            selector: 'id',

        },
        {
            name: 'Date',
            selector: 'date',
            sortable: true,
        },
        {
            name: 'Start Time',
            selector: 'startTime',
            sortable: false,
        },
        {
            name: 'Fault Number',
            selector: 'faultId',
            sortable: true,
        },
        {
            name: 'State',
            selector: 'faultState',
            sortable: true,
        },
        {
            name: 'Skill Rating',
            selector: 'skillRating',
            sortable: true,
            center: true,
        }
    ], [])

    const conditionalRowStyles: IDataTableConditionalRowStyles[] = useMemo(() => [
        {
            when: (row: FaultHistoryItem) => row.id === selectedFaultAttempt.id,
            style: SelectedRowStyle,
        }
    ], [selectedFaultAttempt])

    if (error) return (
        <div className="alert alert-danger text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} />&nbsp;&nbsp;
            {error}
        </div>
    )
    const TopBar = () => <NavigationBar pageTitle={moduleContent.title} />
    if (!user.userId || !tsState.faultHistory) return (
        <div className="body-override">
            <TopBar />
            <Container className="pre-fault" fluid={true}>

                <LoadingOverlay
                    active={!tsState.faultHistory}
                    spinner={<PropagateLoader color={SimutechOrange} />}
                    classNamePrefix='loader-'
                    text={`Loading your fault history`}
                />

            </Container>
        </div>
    )

    return (
        <div className="body-override">
            <TopBar />
            <Wrapper>
                <TableTitle className={'title'}>
                    {'Your History'}
                </TableTitle>
                <div>
                    <DataTable
                        noHeader={true}
                        columns={columns}
                        data={tsState.faultHistory}
                        highlightOnHover={true}
                        pointerOnHover={true}
                        onRowClicked={onRowClick}
                        striped={true}
                        sortIcon={<FontAwesomeIcon icon={faSortDown} />}
                        pagination={true}
                        paginationPerPage={10}
                        paginationComponentOptions={{ noRowsPerPage: true }}
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        dense={true}
                        noDataComponent={<NoDataComponent />}
                    />
                </div>
                <ButtonGroup>
                    <PrimaryButton onClick={goBack}>
                        {'Back'}
                    </PrimaryButton>
                    {
                        tsState.faultHistory.length && (
                            <>
                                <SecondaryButton
                                    disabled={!selectedFaultAttempt.id}
                                    onClick={handleRetry}
                                >
                                    {'Retry Fault'}
                                </SecondaryButton>
                                <SecondaryButton
                                    disabled={!selectedFaultAttempt.id}
                                    onClick={handleReview}
                                >
                                    {'Review Attempt'}
                                </SecondaryButton>
                            </>
                        )

                    }
                </ButtonGroup>

            </Wrapper>
        </div>
    )
}
export default FaultHistory

const NoDataComponent = () => (
    <NoData>
        <div className="alert alert-warning text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} />&nbsp;&nbsp;
            {`You have no history.`}
        </div>
    </NoData>
)

const NoData = styled.div({
    display: 'flex',
    height: '430px',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    fontSize: '1.25em',
})


const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    height: '100%',
    margin: 'auto',
    padding: '1em'
})

const TableTitle = styled.div({
    fontSize: '2.5em',
    color: 'white',
    marginBottom: '1em'
})

const customStyles: IDataTableStyles = {

    tableWrapper: {
        style: {
            minWidth: '800px',
            maxWidth: '900px',
            width: '100%',
            height: '100%',
            margin: 'auto',
        },
    },
    header: {
        style: {
            fontSize: '2.5em',
            textAlign: 'center',
            backgroundColor: 'transparent',
            color: 'white',
            padding: '1em'
        }
    },
    rows: {
        highlightOnHoverStyle: {
            backgroundColor: SimutechBlueHighlight,
            color: SimutechOrangeActive
        }
    },
    headRow: {
        style: {
            paddingTop: '1em',
            paddingBottom: '1em',
            backgroundColor: SimutechBlueHighlight,

        },
    },
    headCells: {
        style: {
            color: SimutechBlue,
            fontSize: '1em',
            fontWeight: 600,
        }
    },
    cells: {
        style: {
            fontSize: '1.25em',
        },
    },
    pagination: {
        style: {
            backgroundColor: SimutechBlueHighlight,
            color: SimutechBlue,
            maxWidth: '900px',
            margin: 'auto',
        },
    }
}
